import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';

interface MergeTag {
  tag: string;
  label: string;
  category: string;
}

const MERGE_TAGS: MergeTag[] = [
  { tag: '{{memberName}}', label: 'Full Name', category: 'Member' },
  { tag: '{{memberFirstName}}', label: 'First Name', category: 'Member' },
  { tag: '{{memberLastName}}', label: 'Last Name', category: 'Member' },
  { tag: '{{memberEmail}}', label: 'Email', category: 'Member' },
  { tag: '{{organizationName}}', label: 'Organization Name', category: 'Organization' },
  { tag: '{{organizationEmail}}', label: 'Organization Email', category: 'Organization' },
  { tag: '{{portalUrl}}', label: 'Portal URL', category: 'Organization' },
  { tag: '{{currentDate}}', label: 'Current Date', category: 'Date' },
  { tag: '{{currentYear}}', label: 'Current Year', category: 'Date' },
];

interface MergeTagPickerProps {
  onInsert: (tag: string) => void;
  className?: string;
}

export function MergeTagPicker({ onInsert, className }: MergeTagPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const categories = Array.from(new Set(MERGE_TAGS.map((t) => t.category)));

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        <span className="text-xs font-mono text-primary-600">{'{{'}</span>
        Merge Tags
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 z-50 w-72 bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <p className="text-xs text-gray-500">Click a tag to insert it at the cursor</p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {categories.map((category) => (
              <div key={category}>
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                  {category}
                </div>
                {MERGE_TAGS.filter((t) => t.category === category).map((tag) => (
                  <button
                    key={tag.tag}
                    type="button"
                    onClick={() => {
                      onInsert(tag.tag);
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700"
                  >
                    <span>{tag.label}</span>
                    <code className="text-xs font-mono text-gray-400">{tag.tag}</code>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
